import type { FC } from "react";
import { useMemo } from "react";

import { type Translate, useTranslations } from "@ui/providers/useTranslations";

import { useConfig } from "@jti/app-context/useConfig";
import type { Config } from "@jti/fe-common";
import { hasFreeTrialDashboard, isCommerceInactive } from "@jti/fe-common";

import { LinkElement } from "./LinkElement";

import styles from "./NavBar.module.scss";

type NavBarType = {
  hasLoyalty: boolean;
  isRasActive: boolean;
  isExternal?: boolean;
  hideMyAddress?: boolean;
  onClick?: (link: NavBarElementType) => void;
};

export type NavBarElementType = {
  name: string;
  to?: string;
  text: string;
  dataTestId: string;
  inactive?: boolean;
  isHidden?: boolean;
  external?: string;
  alwaysExternal?: boolean;
};

type NavBarConfig = Pick<
  Config,
  | "PROFILE_PAGE_URL"
  | "enableSubscriptions"
  | "LOYALTY_PROFILE_PAGE_URL"
  | "LOYALTY_REFERRALS_PAGE_URL"
  | "IS_LOYALTY_REFERRALS_ENABLED"
  | "DISABLE_USER_DASHBOARD_DEVICES"
  | "FREE_TRIAL_USER_DASHBOARD_SHOW"
  | "DISABLE_USER_DASHBOARD_FREE_TRIAL"
  | "FREE_TRIAL_FEATURE_FLAG"
  | "trials-store-view"
>;

const getLinks = (
  __: Translate,
  isRasActive = false,
  hasLoyalty = false,
  config: NavBarConfig,
  hideMyAddress = false
): NavBarElementType[] => {
  const profilePageUrl = config.PROFILE_PAGE_URL || "/profile.html";
  return [
    {
      name: "dashboard",
      to: "/",
      external: profilePageUrl,
      text: __("com.nav_bar.dashboard.title"),
      dataTestId: "accountDashboard",
    },
    {
      name: "orders",
      to: "/orders",
      external: profilePageUrl,
      text: __("com.nav_bar.orders.title"),
      inactive: isCommerceInactive(),
      dataTestId: "myOrders",
    },
    {
      name: "subscription",
      to: "/subscription",
      external: profilePageUrl,
      text: __("com.nav_bar.subscription.title"),
      inactive: !config.enableSubscriptions,
      dataTestId: "mySubscription",
    },
    {
      inactive: !hasFreeTrialDashboard(config),
      name: "trials",
      to: "/free-trial",
      external: profilePageUrl,
      text: __("com.nav_bar.orders.free.title"),
      dataTestId: "myTrialOrders",
    },
    {
      name: "loyalty",
      external: config.LOYALTY_PROFILE_PAGE_URL || "/loyalty/account.html",
      text: __("com.nav_bar.orders.ploomClub"),
      dataTestId: "ploom-club",
      inactive: !hasLoyalty,
      alwaysExternal: true,
    },
    {
      name: "referrals",
      external: config.LOYALTY_REFERRALS_PAGE_URL || "/loyalty/my-referrals.html",
      text: __("com.nav_bar.orders.myReferrals"),
      dataTestId: "my-referrals",
      inactive: config.IS_LOYALTY_REFERRALS_ENABLED !== "true" || !isRasActive,
      alwaysExternal: true,
    },
    {
      name: "account",
      to: "/account",
      external: profilePageUrl,
      text: __("com.nav_bar.account.title"),
      dataTestId: "accountinfo",
    },
    {
      name: "address",
      external: profilePageUrl,
      to: "/address",
      text: __("com.nav_bar.address.title"),
      dataTestId: "myAdresses",
      inactive: hideMyAddress,
    },
    {
      inactive: !!config.DISABLE_USER_DASHBOARD_DEVICES,
      name: "devices",
      to: "/devices",
      external: profilePageUrl,
      text: __("com.nav_bar.devices.title"),
      dataTestId: "myDevices",
    },
    {
      name: "communication",
      to: "/communication",
      external: profilePageUrl,
      text: __("com.nav_bar.communication.title"),
      dataTestId: "communicationPreferences",
    },
  ].filter((a) => !a.inactive);
};

export const NavBar: FC<NavBarType> = ({
  isRasActive = false,
  hasLoyalty = false,
  isExternal = false,
  hideMyAddress = false,
  onClick,
}) => {
  const config = useConfig();
  const { __ } = useTranslations();
  const links = useMemo(
    () => getLinks(__, isRasActive, hasLoyalty, config, hideMyAddress),
    [__, hasLoyalty, config, isRasActive, hideMyAddress]
  );

  return (
    <nav className={styles.container}>
      <ul className={styles.list} data-testid="accountDashboardBlock">
        {links.map((link) => {
          if (!link.text || link.text.length === 0) {
            return null;
          }

          return (
            <li key={link.name} data-testid={link.dataTestId} {...(onClick && { onClick: () => onClick(link) })}>
              <LinkElement link={link} isExternal={isExternal} alwaysExternal={!!link.alwaysExternal} />
            </li>
          );
        })}
      </ul>
    </nav>
  );
};
